/**
 * The typeahead's Algolia query for one entity's records. `useEntityRecordSearch` calls it for the
 * record picker and merges the hits with its cache; `useAddressSearch` calls it once per configured
 * address record source, fanning a single query across several entities.
 *
 * It is not the app's only Algolia record query: `useRecordsBrowser` runs its own, with its own
 * `buildFilters` and its own copy of this hit shape, because the browser pages its results and
 * filters them by status. Neither clause belongs on a typeahead, so the two stay unmerged.
 *
 * An empty query, an empty `entityId`, a missing index name or a missing search client all resolve
 * to `[]`. Only a network or Algolia failure rejects, and the address control depends on that split
 * to tell a source that is broken from one that matched nothing.
 */

import { searchClient } from './searchConfig';
import { algoliaConfig } from '../config';
import type { Fields } from '#types';
import { DEFAULT_SEARCH_HITS_PER_PAGE } from '#types';

/**
 * One record as the search index holds it.
 */
export type AlgoliaRecordHit = {
  objectID: string;
  name?: string;
  status?: string;
  entityGroupIds?: string[];
  /** Records are indexed whole, so field values ride along on the hit — except oversized ones, which Algolia truncates. */
  fields?: Fields;
  _parentDocId?: string;
  _collectionName?: string;
  meta?: {
    searchNameLowercase?: string;
    lastModified?: any;
  };
};

/**
 * The Algolia filter string for one entity's records, optionally narrowed to one entity group.
 */
const buildFilters = (entityId: string, entityGroupId?: string): string =>
  [
    '_collectionName:records',
    ...(entityId ? [`_parentDocId:${entityId}`] : []),
    ...(entityGroupId ? [`entityGroupIds:${entityGroupId}`] : []),
  ].join(' AND ');

export const searchEntityRecords = async ({
  entityId,
  entityGroupId,
  query,
  hitsPerPage = DEFAULT_SEARCH_HITS_PER_PAGE,
}: {
  entityId: string;
  entityGroupId?: string;
  query: string;
  hitsPerPage?: number;
}): Promise<AlgoliaRecordHit[]> => {
  if (!query.trim() || !entityId || !algoliaConfig?.allIndexName) {
    return [];
  }

  if (!searchClient) {
    console.error('Algolia search client not configured');
    return [];
  }

  const { results } = await searchClient.search<AlgoliaRecordHit>({
    requests: [{
      indexName: algoliaConfig.allIndexName,
      query,
      filters: buildFilters(entityId, entityGroupId),
      hitsPerPage,
    }],
  });

  const firstResult = results[0];
  if ('hits' in firstResult) {
    return firstResult.hits;
  }

  return [];
};
